import type { Shell } from '#packages/apilib';
import { EditorAPIKey } from '#packages/apis';
import {
  EditorPlatformHostIntegrationAPI,
  type PluginInfo,
  type WidgetPointer,
  type WidgetSlot,
  type PluginActionContext,
} from '@wix/editor-platform-host-integration-apis';
import type { CompRef } from 'types/documentServices';
import { isInViewport } from './widgetPluginsUtils';

export default (shell: Shell) => {
  const editorAPI = shell.getAPI(EditorAPIKey);

  function getWidgetSlot(
    slot: CompRef | { hostCompRef: CompRef; slotId: string },
    options: { includePrivate: boolean },
  ): WidgetSlot {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getWidgetSlot(slot, options);
  }

  function hasWidgetSlot(widgetCompRef: CompRef): boolean {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.hasWidgetSlot(widgetCompRef);
  }

  function isSlotPublic(slotCompRef: CompRef): boolean {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.isSlotPublic(slotCompRef);
  }

  function getWidgetSlots(
    widgetCompRef: CompRef,
    options: { includePrivate: boolean },
  ): WidgetSlot[] {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getWidgetSlots(widgetCompRef, options);
  }

  async function installWidgetPlugin(
    slot: CompRef | { hostCompRef: CompRef; slotId: string },
    widgetPointer: WidgetPointer,
    context: PluginActionContext,
  ): Promise<CompRef> {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return await platformAPI.slots.plugins.install(
      slot,
      widgetPointer,
      context,
    );
  }

  async function uninstallWidgetPlugin(
    slot: CompRef | { hostCompRef: CompRef; slotId: string },
    context: PluginActionContext,
  ): Promise<void> {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return await platformAPI.slots.plugins.uninstall(slot, context);
  }

  async function moveWidgetToAnotherSlot(
    fromSlotComponentRef: CompRef,
    toSlotComponentRef: CompRef,
  ) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    await platformAPI.slots.moveToSlot(
      fromSlotComponentRef,
      toSlotComponentRef,
      {
        biOrigin: 'contextMenu',
        controlMode: 'user',
      },
    );
  }

  function getWidgetSlotsToMoveContent(slotComponentRef: CompRef): Array<{
    slotRef: CompRef;
    pluginInfo: PluginInfo;
    name: string;
    isPopulated: boolean;
    isSelected: boolean;
  }> {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getWidgetSlotsToMoveContent(slotComponentRef);
  }

  function getNearestWidgetHostRef(compRef: CompRef) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getNearestWidgetHostRef(compRef);
  }

  function unhighlightSlot(compRef: CompRef) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.unhighlightSlot(compRef);
  }

  async function showOnPage(compRef: CompRef) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    await platformAPI.slots.plugins.showOnPage(compRef);
    scrollToSlot(compRef);
  }

  async function highlightSlot(compRef: CompRef) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    await platformAPI.slots.highlightSlot(compRef);
    scrollToSlot(compRef);
  }

  function selectSlot(compRef: CompRef) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    const slotSelectionCompRef =
      platformAPI.slots.getCompRefForSlotSelection(compRef);
    editorAPI.selection.selectComponentByCompRef(slotSelectionCompRef);
  }

  function scrollToSlot(compRef: CompRef) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    const slotPosition = platformAPI.slots.getSlotPosition(compRef);

    if (isInViewport(editorAPI, slotPosition)) {
      return;
    }

    const positionOffset = 30;
    editorAPI.ui.scroll.animateScroll(
      {
        y: slotPosition.y - positionOffset,
      },
      0.8,
    );
  }

  function isSlotContentPotentialMovable(
    slotsPlaceholderCompRef: CompRef,
  ): boolean {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.isSlotContentPotentialMovable(
      slotsPlaceholderCompRef,
    );
  }

  function isWidgetPluginComponent(widgetPluginCompRef: CompRef): boolean {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.plugins.isPluginComponent(widgetPluginCompRef);
  }

  function isRefComponentVerticalApp(refComponentPointer: CompRef): boolean {
    const { appDefinitionId } =
      editorAPI.components.data.get(refComponentPointer);
    const appData = editorAPI.platform.getAppDataByAppDefId(appDefinitionId);
    if (!appData) return false;
    return appData.components?.some(({ type }) => type === 'PLATFORM');
  }

  function getSlotPlaceholderRefByContentRef(
    compRef: CompRef,
  ): CompRef | undefined {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getSlotPlaceholderRefByContentRef(compRef);
  }

  function getWidgetHostRefByWidgetRef(compRef: CompRef): CompRef {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getWidgetHostRefByWidgetRef(compRef);
  }

  function getWidgetHostRefBySlotRef(compRef: CompRef): CompRef {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getWidgetHostRefBySlotRef(compRef);
  }

  function getSlotInBlocks(slotCompRef: CompRef): WidgetSlot | undefined {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getSlotInBlocksAppEditor(slotCompRef);
  }

  return {
    hasWidgetSlot,
    isSlotPublic,
    getWidgetSlot,
    getSlotInBlocks,
    getWidgetSlots,
    installWidgetPlugin,
    uninstallWidgetPlugin,
    moveWidgetToAnotherSlot,
    scrollToSlot,
    getWidgetSlotsToMoveContent,
    getNearestWidgetHostRef,
    highlightSlot,
    unhighlightSlot,
    showOnPage,
    selectSlot,
    isSlotContentPotentialMovable,
    isWidgetPluginComponent,
    getSlotPlaceholderRefByContentRef,
    getWidgetHostRefByWidgetRef,
    getWidgetHostRefBySlotRef,
    isRefComponentVerticalApp,
  };
};
